import { useEffect, useMemo, useRef, useState } from 'react';
import { Package, Search, SlidersHorizontal } from 'lucide-react';
import VariantGroup from './VariantGroup';
import BulkActionsBar from './BulkActionsBar';
import ShopSelect from '../../../components/ShopSelect';
import {
  buildVariantGroups,
  filterVariants,
  sortVariants,
  getVariantStockState,
  inferOptionKeys,
  distinctValuesForKey,
} from './prModalUtils';

const inputCls =
  'w-full rounded-xl border border-neutral-200 bg-white px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-neutral-400 dark:border-neutral-700 dark:bg-neutral-800 dark:text-neutral-100';

export default function VariantList({
  variants,
  selectedMap,
  productThreshold,
  search,
  onSearchChange,
  stockFilter,
  onStockFilterChange,
  sortBy,
  onSortByChange,
  attrFilters,
  onAttrFilterChange,
  groupByKey,
  onGroupByKeyChange,
  includeInactive,
  onIncludeInactiveChange,
  collapsedGroups,
  onToggleGroup,
  onToggleVariant,
  onUpdateVariantQty,
  onSelectAll,
  onSelectLowStock,
  onClearSelection,
  selectedCount,
  bulkQuantity,
  onBulkQuantityChange,
  onApplyBulk,
}) {
  const [filtersOpen, setFiltersOpen] = useState(false);
  const filterPopoverRef = useRef(null);

  const optionKeys = useMemo(() => inferOptionKeys(variants), [variants]);

  const filterGroups = useMemo(
    () => optionKeys
      .map((key) => ({ name: key, values: distinctValuesForKey(variants, key) }))
      .filter((group) => group.values.length > 0),
    [optionKeys, variants],
  );

  const activeAttrFilters = useMemo(
    () => Object.entries(attrFilters || {})
      .filter(([, value]) => value && value !== 'all')
      .map(([key, value]) => ({ key, value: String(value) })),
    [attrFilters],
  );

  useEffect(() => {
    if (!filtersOpen) return undefined;

    const onDocClick = (event) => {
      if (!filterPopoverRef.current) return;
      if (filterPopoverRef.current.contains(event.target)) return;
      if (event.target instanceof Element && event.target.closest('[role="listbox"]')) return;
      setFiltersOpen(false);
    };
    const onEsc = (event) => {
      if (event.key === 'Escape') setFiltersOpen(false);
    };

    document.addEventListener('pointerdown', onDocClick);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('pointerdown', onDocClick);
      document.removeEventListener('keydown', onEsc);
    };
  }, [filtersOpen]);

  const handleToggleAttrFilter = (key, value) => {
    const nextValue = String(value);
    const currentValue = String((attrFilters || {})[key] || 'all');
    onAttrFilterChange(key, currentValue === nextValue ? 'all' : nextValue);
  };

  const handleClearAllAttrFilters = () => {
    activeAttrFilters.forEach(({ key }) => onAttrFilterChange(key, 'all'));
  };

  const filteredSorted = useMemo(() => {
    const filtered = filterVariants(
      variants,
      { query: search, stockFilter, attrFilters, includeInactive },
      productThreshold,
    );
    return sortVariants(filtered, sortBy, productThreshold);
  }, [variants, search, stockFilter, attrFilters, includeInactive, sortBy, productThreshold]);

  const groupedFiltered = useMemo(
    () => buildVariantGroups(filteredSorted, groupByKey).groups,
    [filteredSorted, groupByKey],
  );

  const totalLowOrOut = useMemo(
    () => variants.filter((v) => {
      const s = getVariantStockState(v, productThreshold);
      return s === 'OUT' || s === 'LOW' || s === 'NEGATIVE';
    }).length,
    [variants, productThreshold],
  );

  return (
    <div className="overflow-hidden rounded-2xl border border-neutral-200 bg-white dark:border-neutral-700 dark:bg-neutral-900/40">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-neutral-200 px-3.5 py-3 dark:border-neutral-700">
        <div>
          <h4 className="flex items-center gap-1.5 text-sm font-semibold text-neutral-900 dark:text-neutral-100">
            <Package className="h-4 w-4 text-neutral-500" /> Choose variants
          </h4>
          <p className="mt-0.5 text-[11px] text-neutral-500 dark:text-neutral-400">Showing {filteredSorted.length} of {variants.length} variants</p>
        </div>
        <div className="flex flex-wrap gap-2 text-[11px]">
          <button type="button" onClick={onSelectLowStock} className="rounded-lg border border-amber-300 bg-amber-50 px-2.5 py-1.5 font-semibold text-amber-700 hover:bg-amber-100 dark:border-amber-700 dark:bg-amber-950/40 dark:text-amber-300">Select low stock</button>
          <button type="button" onClick={onSelectAll} className="rounded-lg border border-neutral-300 bg-white px-2.5 py-1.5 font-medium hover:bg-neutral-100 dark:border-neutral-600 dark:bg-neutral-900 dark:hover:bg-neutral-800">Select all</button>
          {selectedCount > 0 && <button type="button" onClick={onClearSelection} className="rounded-lg border border-neutral-300 bg-white px-2.5 py-1.5 font-medium text-neutral-600 hover:bg-neutral-100 dark:border-neutral-600 dark:bg-neutral-900 dark:text-neutral-300 dark:hover:bg-neutral-800">Clear</button>}
        </div>
      </div>

      <div className="space-y-3 p-3">
        <div className="grid gap-2 md:grid-cols-[minmax(220px,1fr)_180px_auto]">
          <div className="relative min-w-0">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-neutral-400" />
            <input type="text" value={search} onChange={(e) => onSearchChange(e.target.value)} placeholder="Search variant or SKU..." className={inputCls + ' pl-8 text-xs'} />
          </div>
          <ShopSelect value={stockFilter} onValueChange={onStockFilterChange} options={[{ value: 'all', label: 'All stock states' }, { value: 'out', label: 'Out of stock' }, { value: 'low', label: 'Low stock' }, { value: 'sufficient', label: 'Sufficient stock' }]} ariaLabel="Filter variants by stock state" align="start" minPanelWidth={180} triggerClassName="min-h-10 w-full rounded-xl px-3 py-2 text-xs" panelClassName="py-1" optionClassName="text-xs" />

          <div className="relative" ref={filterPopoverRef}>
            <button type="button" onClick={() => setFiltersOpen((value) => !value)} className="inline-flex min-h-10 w-full items-center justify-center gap-2 rounded-xl border border-neutral-300 bg-white px-3 py-2 text-xs font-semibold text-neutral-700 hover:bg-neutral-50 dark:border-neutral-600 dark:bg-neutral-900 dark:text-neutral-100 dark:hover:bg-neutral-800">
              <SlidersHorizontal className="h-3.5 w-3.5" /> More filters{activeAttrFilters.length ? ` (${activeAttrFilters.length})` : ''}
            </button>

            {filtersOpen && (
              <div className="absolute right-0 top-12 z-30 w-[min(22rem,calc(100vw-2rem))] rounded-2xl border border-neutral-200 bg-white p-3 shadow-xl dark:border-neutral-700 dark:bg-neutral-900">
                <div className="mb-3 flex items-center justify-between">
                  <div className="text-xs font-semibold text-neutral-900 dark:text-neutral-100">More filters</div>
                  {activeAttrFilters.length > 0 && <button type="button" onClick={handleClearAllAttrFilters} className="text-[11px] font-medium text-neutral-500 underline dark:text-neutral-400">Clear attributes</button>}
                </div>
                <div className="max-h-[min(26rem,calc(100dvh-10rem))] space-y-3 overflow-y-auto pr-1">
                  {optionKeys.length > 1 && (
                    <div>
                      <div className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-neutral-500 dark:text-neutral-400">Grouping</div>
                      <ShopSelect value={groupByKey || optionKeys[0] || ''} onValueChange={(value) => onGroupByKeyChange(value || null)} options={optionKeys.map((key) => ({ value: key, label: `Group by: ${key}` }))} ariaLabel="Group variants" align="start" minPanelWidth={200} triggerClassName="min-h-10 w-full rounded-xl px-3 py-2 text-xs" panelClassName="py-1" optionClassName="text-xs" />
                    </div>
                  )}
                  <div>
                    <div className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-neutral-500 dark:text-neutral-400">Sorting</div>
                    <ShopSelect value={sortBy} onValueChange={onSortByChange} options={[{ value: 'stockAsc', label: 'Stock low → high' }, { value: 'stockDesc', label: 'Stock high → low' }, { value: 'label', label: 'Variant label' }, { value: 'sku', label: 'SKU' }]} ariaLabel="Sort variants" align="start" minPanelWidth={200} triggerClassName="min-h-10 w-full rounded-xl px-3 py-2 text-xs" panelClassName="py-1" optionClassName="text-xs" />
                  </div>
                  {filterGroups.map((group) => (
                    <div key={group.name}>
                      <div className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-neutral-500 dark:text-neutral-400">{group.name}</div>
                      <div className="flex flex-wrap gap-1.5">
                        {group.values.map((value) => {
                          const token = `${group.name}:${String(value)}`;
                          const active = activeAttrFilters.some((filter) => `${filter.key}:${filter.value}` === token);
                          return <button key={token} type="button" onClick={() => handleToggleAttrFilter(group.name, value)} className={`rounded-full border px-2 py-1 text-[11px] font-medium ${active ? 'border-neutral-900 bg-neutral-900 text-white dark:border-neutral-100 dark:bg-neutral-100 dark:text-neutral-900' : 'border-neutral-300 bg-white text-neutral-700 hover:bg-neutral-50 dark:border-neutral-600 dark:bg-neutral-900 dark:text-neutral-200 dark:hover:bg-neutral-800'}`}>{String(value)}</button>;
                        })}
                      </div>
                    </div>
                  ))}
                  <label className="flex items-center gap-2 rounded-xl border border-neutral-200 px-3 py-2 text-xs text-neutral-700 dark:border-neutral-700 dark:text-neutral-200">
                    <input type="checkbox" checked={includeInactive} onChange={(e) => onIncludeInactiveChange(e.target.checked)} />
                    Show inactive variants
                  </label>
                </div>
              </div>
            )}
          </div>
        </div>

        {activeAttrFilters.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {activeAttrFilters.map((chip) => (
              <button key={`${chip.key}:${chip.value}`} type="button" onClick={() => onAttrFilterChange(chip.key, 'all')} className="inline-flex items-center gap-1 rounded-full border border-neutral-300 bg-neutral-50 px-2.5 py-1 text-[11px] font-medium text-neutral-700 hover:bg-neutral-100 dark:border-neutral-600 dark:bg-neutral-800 dark:text-neutral-200">
                {chip.key}: {chip.value} <span aria-hidden="true">×</span>
              </button>
            ))}
          </div>
        )}

        <BulkActionsBar selectedCount={selectedCount} bulkQuantity={bulkQuantity} onChangeBulkQuantity={onBulkQuantityChange} onApplyBulk={onApplyBulk} onClearSelection={onClearSelection} />

      {variants.length === 0 ? (
        <div className="rounded-lg border border-dashed border-neutral-300 bg-white px-3 py-6 text-center text-xs text-neutral-500 dark:border-neutral-700 dark:bg-neutral-900/40 dark:text-neutral-400">
          This product has no variants. Add variants before creating a variant-level requisition.
        </div>
      ) : groupedFiltered.length === 0 ? (
        <div className="rounded-lg border border-dashed border-neutral-300 bg-white px-3 py-6 text-center text-xs text-neutral-500 dark:border-neutral-700 dark:bg-neutral-900/40 dark:text-neutral-400">
          No variants match the current search/filter.
        </div>
      ) : (
        <div className="space-y-2">
          {groupedFiltered.map((group) => (
            <VariantGroup
              key={group.key}
              group={group}
              collapsed={collapsedGroups.has(group.key)}
              onToggle={() => onToggleGroup(group.key)}
              selectedMap={selectedMap}
              onToggleVariant={onToggleVariant}
              onUpdateVariantQty={onUpdateVariantQty}
              productThreshold={productThreshold}
            />
          ))}
        </div>
      )}

        <div className="border-t border-neutral-200 pt-2 text-[11px] text-neutral-600 dark:border-neutral-700 dark:text-neutral-300">
          {totalLowOrOut} variant(s) currently low/out-of-stock based on configured thresholds.
        </div>
      </div>
    </div>
  );
}
